import { motion } from 'framer-motion'
import { FileText, Sparkles, MessageSquare, Zap } from 'lucide-react'

const CustomInstructions = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="prose prose-invert max-w-none"
    >
      <h1 className="text-3xl font-bold mb-6 flex items-center">
        <FileText className="mr-3 text-accent-cyan" size={32} />
        自定义指令
      </h1>

      {/* 指令介绍 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">什么是自定义指令</h2>
        <p className="text-gray-400 mb-4">
          自定义指令是预设的系统提示词，用于定制 AI 的行为风格、角色设定和回答方式。通过添加指令，你可以让 AI 更符合你的使用习惯。
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose">
          <div className="glass-effect rounded-lg p-5 border-l-4 border-blue-500">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <MessageSquare className="mr-2 text-blue-400" size={24} />
              聊天模式指令
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              控制 AI 在聊天模式下的行为：
            </p>
            <ul className="text-xs text-gray-400 space-y-1 ml-4">
              <li>• 角色扮演（猫娘、助手、老师等）</li>
              <li>• 对话风格（正式、幽默、简洁等）</li>
              <li>• 回答方式（详细、简短、举例等）</li>
            </ul>
          </div>

          <div className="glass-effect rounded-lg p-5 border-l-4 border-purple-500">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Zap className="mr-2 text-purple-400" size={24} />
              任务模式指令
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              控制 AI 在任务模式下的行为：
            </p>
            <ul className="text-xs text-gray-400 space-y-1 ml-4">
              <li>• 任务执行规则（谨慎、高效等）</li>
              <li>• 工具使用偏好</li>
              <li>• 错误处理方式</li>
            </ul>
          </div>
        </div>
      </section>

      {/* 添加指令 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">添加指令</h2>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              1
            </div>
            <div>
              <h3 className="font-semibold mb-2">打开指令管理</h3>
              <p className="text-gray-400 text-sm">
                打开设置窗口，进入"指令"页面，点击"添加指令"按钮。
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              2
            </div>
            <div>
              <h3 className="font-semibold mb-2">填写指令信息</h3>
              <ul className="text-sm text-gray-400 space-y-2 ml-4">
                <li className="flex items-start">
                  <span className="text-accent-cyan mr-2">•</span>
                  <span><strong className="text-white">指令名称：</strong>给指令起一个容易识别的名字</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent-cyan mr-2">•</span>
                  <span><strong className="text-white">指令类型：</strong>选择聊天模式或任务模式</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent-cyan mr-2">•</span>
                  <span><strong className="text-white">指令内容：</strong>详细描述你希望 AI 如何表现</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent-cyan mr-2">•</span>
                  <span><strong className="text-white">启用状态：</strong>选择是否立即启用</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              3
            </div>
            <div>
              <h3 className="font-semibold mb-2">保存并生效</h3>
              <p className="text-gray-400 text-sm">
                点击"添加"按钮保存。启用的指令会在对应模式下自动生效。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 指令示例 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">指令示例</h2>
        
        <div className="space-y-4">
          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Sparkles className="mr-2 text-yellow-400" size={20} />
              聊天模式示例
            </h3>
            
            <div className="space-y-3 text-sm">
              <div className="p-4 bg-blue-500/10 rounded border border-blue-500/30">
                <p className="text-blue-300 font-semibold mb-2">📝 可爱猫娘助手</p>
                <p className="text-gray-400 mb-2"><strong>类型：</strong>聊天模式</p>
                <p className="text-gray-400 mb-2"><strong>内容：</strong></p>
                <div className="p-3 bg-black/30 rounded text-xs text-gray-300">
                  你现在是一个可爱的猫娘助手，说话要带上"喵~"，语气要活泼可爱。
                  在回答问题时保持专业，但要用轻松愉快的方式表达。
                </div>
              </div>

              <div className="p-4 bg-blue-500/10 rounded border border-blue-500/30">
                <p className="text-blue-300 font-semibold mb-2">🎓 专业导师</p>
                <p className="text-gray-400 mb-2"><strong>类型：</strong>聊天模式</p>
                <p className="text-gray-400 mb-2"><strong>内容：</strong></p>
                <div className="p-3 bg-black/30 rounded text-xs text-gray-300">
                  你是一位经验丰富的导师，擅长用简单易懂的方式解释复杂概念。
                  回答问题时要循序渐进，多举实例，并在适当时候提出启发性问题。
                </div>
              </div>

              <div className="p-4 bg-blue-500/10 rounded border border-blue-500/30">
                <p className="text-blue-300 font-semibold mb-2">💼 简洁高效</p>
                <p className="text-gray-400 mb-2"><strong>类型：</strong>聊天模式</p>
                <p className="text-gray-400 mb-2"><strong>内容：</strong></p>
                <div className="p-3 bg-black/30 rounded text-xs text-gray-300">
                  回答要简洁明了，直击要点。除非用户明确要求详细解释，
                  否则用最少的文字传达最核心的信息。使用列表和要点格式。
                </div>
              </div>
            </div>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Sparkles className="mr-2 text-yellow-400" size={20} />
              任务模式示例
            </h3>
            
            <div className="space-y-3 text-sm">
              <div className="p-4 bg-purple-500/10 rounded border border-purple-500/30">
                <p className="text-purple-300 font-semibold mb-2">⚡ 高效执行</p>
                <p className="text-gray-400 mb-2"><strong>类型：</strong>任务模式</p>
                <p className="text-gray-400 mb-2"><strong>内容：</strong></p>
                <div className="p-3 bg-black/30 rounded text-xs text-gray-300">
                  执行任务时要快速高效，优先使用最直接的方法。
                  不要过多解释过程，专注于完成任务并给出结果。
                  如果遇到问题，立即报告而不是尝试多次。
                </div>
              </div>

              <div className="p-4 bg-purple-500/10 rounded border border-purple-500/30">
                <p className="text-purple-300 font-semibold mb-2">🛡️ 谨慎安全</p>
                <p className="text-gray-400 mb-2"><strong>类型：</strong>任务模式</p>
                <p className="text-gray-400 mb-2"><strong>内容：</strong></p>
                <div className="p-3 bg-black/30 rounded text-xs text-gray-300">
                  在执行任何可能影响系统或数据的操作前，要详细说明将要做什么。
                  对于删除、修改等危险操作，要特别谨慎，并在执行前确认。
                  优先使用安全的方法，避免可能造成损失的操作。
                </div>
              </div>

              <div className="p-4 bg-purple-500/10 rounded border border-purple-500/30">
                <p className="text-purple-300 font-semibold mb-2">📊 详细报告</p>
                <p className="text-gray-400 mb-2"><strong>类型：</strong>任务模式</p>
                <p className="text-gray-400 mb-2"><strong>内容：</strong></p>
                <div className="p-3 bg-black/30 rounded text-xs text-gray-300">
                  执行任务时要详细记录每一步操作和结果。
                  使用的工具、参数、返回值都要清楚说明。
                  任务完成后提供完整的执行报告和数据分析。
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 编写技巧 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">编写技巧</h2>
        
        <div className="space-y-3">
          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 明确具体</h4>
            <p className="text-sm text-gray-400">
              指令要清晰明确，避免模糊的描述。例如："回答要简洁"不如"每个回答控制在100字以内"。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 举例说明</h4>
            <p className="text-sm text-gray-400">
              在指令中加入示例，让 AI 更好理解你的期望。例如："用这种格式回答：1. 要点一 2. 要点二"。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 分类管理</h4>
            <p className="text-sm text-gray-400">
              为不同场景创建不同的指令，需要时启用对应的指令。避免一个指令包含太多要求。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-semibold mb-2 text-yellow-300">⚠️ 避免冲突</h4>
            <p className="text-sm text-gray-400">
              同时启用多个指令时，确保它们不会相互矛盾。例如不要同时要求"详细解释"和"简洁回答"。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-semibold mb-2 text-yellow-300">⚠️ 测试调整</h4>
            <p className="text-sm text-gray-400">
              添加指令后要测试效果，根据实际表现调整内容。指令不是一次性的，可以随时修改优化。
            </p>
          </div>
        </div>
      </section>

      {/* 管理指令 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">管理指令</h2>
        
        <div className="glass-effect rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-3 font-semibold">操作</th>
                <th className="text-left p-3 font-semibold">说明</th>
              </tr>
            </thead>
            <tbody className="text-gray-400">
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">启用/禁用</td>
                <td className="p-3">通过开关快速启用或禁用指令，无需删除</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">编辑指令</td>
                <td className="p-3">点击编辑按钮修改指令内容</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">删除指令</td>
                <td className="p-3">删除不再需要的指令</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">查看效果</td>
                <td className="p-3">在对话中测试指令是否按预期工作</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 使用建议 */}
      <section className="mt-8 p-6 glass-effect rounded-lg border border-accent-cyan/30">
        <h3 className="text-xl font-semibold mb-4">💡 使用建议</h3>
        <ul className="space-y-2 text-sm text-gray-400">
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">从简单开始：</strong>先创建简单的指令，测试效果后再逐步完善</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">场景化设置：</strong>为不同使用场景（工作、学习、娱乐）创建专门的指令</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">定期优化：</strong>根据使用体验，定期调整和优化指令内容</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">保持精简：</strong>不要创建太多指令，保留最常用和最有效的</span>
          </li>
        </ul>
      </section>
    </motion.div>
  )
}

export default CustomInstructions
